import { useForm } from 'react-hook-form'
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import ImageUpload from '../Common/ImageUpload'
import MultipleImageUpload from '../Common/MultipleImageUpload'
import RichTextEditor from '../Common/RichTextEditor'
import { approvalAPI, degreeAPI, specializationAPI, universityAPI } from '../../services/api'

const UniversityForm = ({ onSubmit, initialData = null, onCancel }) => {
    const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm({
        defaultValues: {}
    })

    const [activeTab, setActiveTab] = useState('basic')
    const [bannerImages, setBannerImages] = useState(initialData?.banner || [])
    const [galleryImages, setGalleryImages] = useState(initialData?.gallery || [])
    const [topRecruiters, setTopRecruiters] = useState(initialData?.top_recruiters || [])
    const [courses, setCourses] = useState(initialData?.courses || [])
    const [showCourseForm, setShowCourseForm] = useState(false)
    const [editingCourse, setEditingCourse] = useState(null)
    const [courseFormData, setCourseFormData] = useState({
        level: '',
        degreeId: '',
        specialization: [],
        duration: '',
        featured: false,
        curriculum: '',
        eligibility: '',
        feeStructure: '',
        scholarships: '',
        order: 0
    })

    // Reset form when initialData changes
    useEffect(() => {
        if (initialData) {
            const formData = {
                ...initialData,
                approvals: Array.isArray(initialData.approvals) ? initialData.approvals.map(approval => approval._id || approval) : [],
                // Handle fee_range object
                fee_range_type: initialData.fee_range?.type || '',
                // Handle logo object
                logo_url: initialData.logo?.url || ''
            }
            reset(formData)
            setBannerImages(initialData.banner || [])
            setGalleryImages(initialData.gallery || [])
            // Convert top recruiters array to textarea format
            const recruitersText = initialData.top_recruiters ?
                initialData.top_recruiters.map(r => r.company_name).join('\n') : '';
            setTopRecruiters([{ company_name: recruitersText }])
        } else {
            reset({})
            setBannerImages([])
            setGalleryImages([])
            setTopRecruiters([{ company_name: '' }])
        }
    }, [initialData, reset])

    // Fetch master data
    const { data: approvalsData } = useQuery({
        queryKey: ['approvals'],
        queryFn: () => approvalAPI.getApprovals()
    })

    const { data: degreesData } = useQuery({
        queryKey: ['degrees'],
        queryFn: () => degreeAPI.getDegrees()
    })

    const { data: specializationsData } = useQuery({
        queryKey: ['specializations', courseFormData.degreeId],
        queryFn: () => specializationAPI.getSpecializations(courseFormData.degreeId ? { degree: courseFormData.degreeId } : {}),
        enabled: !!courseFormData.degreeId
    })

    // Fetch courses for the university when editing
    const { data: universityCoursesData } = useQuery({
        queryKey: ['universityCourses', initialData?._id],
        queryFn: () => universityAPI.getUniversityCourses(initialData._id),
        enabled: !!initialData?._id
    })

    const approvals = approvalsData?.data?.approvals || []
    const degrees = degreesData?.data?.degrees || []
    const specializations = specializationsData?.data?.specializations || []
    const universityCourses = universityCoursesData?.data?.courses || []

    // Set approvals for edit operations after approvals data is loaded
    useEffect(() => {
        if (initialData && initialData.approvals && approvals.length > 0) {
            const approvalIds = Array.isArray(initialData.approvals)
                ? initialData.approvals.map(approval => approval._id || approval)
                : []
            if (approvalIds.length > 0) {
                // Use setTimeout to ensure this runs after the form reset
                setTimeout(() => {
                    setValue('approvals', approvalIds)
                }, 100)
            }
        }
    }, [initialData, approvals, setValue])

    // Update courses when university courses are loaded
    useEffect(() => {
        if (initialData && universityCourses.length > 0) {
            setCourses(universityCourses)
        }
    }, [initialData, universityCourses])

    const tabs = [
        { id: 'basic', name: 'Basic Info' },
        { id: 'content', name: 'Content' },
        { id: 'courses', name: 'Courses' },
        { id: 'recruiters', name: 'Top Recruiters' },
        { id: 'gallery', name: 'Gallery' }
    ]

    const addBannerImage = () => {
        setBannerImages([...bannerImages, { url: '', caption: '' }])
    }

    const removeBannerImage = (index) => {
        setBannerImages(bannerImages.filter((_, i) => i !== index))
    }

    const updateBannerImage = (index, field, value) => {
        const updated = [...bannerImages]
        updated[index][field] = value
        setBannerImages(updated)
    }

    const addGalleryImage = () => {
        setGalleryImages([...galleryImages, { url: '', caption: '', type: 'campus' }])
    }

    const removeGalleryImage = (index) => {
        setGalleryImages(galleryImages.filter((_, i) => i !== index))
    }

    const updateGalleryImage = (index, field, value) => {
        const updated = [...galleryImages]
        updated[index][field] = value
        setGalleryImages(updated)
    }

    const updateTopRecruiter = (index, field, value) => {
        const updated = [...topRecruiters]
        updated[index][field] = value
        setTopRecruiters(updated)
    }

    const addCourse = () => {
        setEditingCourse(null)
        setCourseFormData({
            level: '',
            degreeId: '',
            specialization: [],
            duration: '',
            featured: false,
            curriculum: '',
            eligibility: '',
            feeStructure: '',
            scholarships: '',
            order: 0
        })
        setShowCourseForm(true)
    }

    const editCourse = (course) => {
        setEditingCourse(course)
        // Map course data to form data structure
        const courseFormData = {
            level: course.level || '',
            degreeId: course.degreeId ? (typeof course.degreeId === 'object' ? course.degreeId._id : course.degreeId) : '',
            specialization: course.specialization || [],
            duration: course.duration || '',
            featured: course.featured || false,
            curriculum: course.curriculum || '',
            eligibility: course.eligibility || '',
            feeStructure: course.feeStructure || '',
            scholarships: course.scholarships || '',
            order: course.order || 0
        }
        setCourseFormData(courseFormData)
        setShowCourseForm(true)
    }

    const removeCourse = (index) => {
        setCourses(courses.filter((_, i) => i !== index))
    }

    const handleCourseSubmit = () => {
        // Validate required fields
        if (!courseFormData.level || !courseFormData.degreeId || !courseFormData.duration || !courseFormData.specialization.length) {
            alert('Please fill in all required fields: Level, Degree, Duration, and at least one Specialization')
            return
        }

        if (editingCourse) {
            // Update existing course - preserve database fields like _id, createdAt, etc.
            const updatedCourses = courses.map((course) =>
                course._id === editingCourse._id ? {
                    ...course, // Preserve all existing fields
                    ...courseFormData, // Update with new form data
                    universityId: initialData?._id || course.universityId
                } : course
            )
            setCourses(updatedCourses)
        } else {
            // Add new course
            const newCourse = { ...courseFormData, universityId: initialData?._id || null }
            setCourses([...courses, newCourse])
        }
        setShowCourseForm(false)
        setEditingCourse(null)
    }

    const handleCourseCancel = () => {
        setShowCourseForm(false)
        setEditingCourse(null)
    }

    const updateCourseFormData = (field, value) => {
        setCourseFormData(prev => {
            const updated = {
                ...prev,
                [field]: value
            }

            // Reset degree and specializations when level changes
            if (field === 'level') {
                updated.degreeId = ''
                updated.specialization = []
                updated.duration = ''
            }
            // Reset specializations and set duration when degree changes
            else if (field === 'degreeId') {
                updated.specialization = []
                // Auto-set duration from selected degree
                const selectedDegree = degrees.find(d => d._id === value)
                if (selectedDegree && selectedDegree.duration) {
                    updated.duration = selectedDegree.duration
                } else {
                    updated.duration = ''
                }
            }

            return updated
        })
    }

    const handleFormSubmit = (data) => {
        // Process top recruiters - split textarea content into individual companies
        const recruiterText = topRecruiters[0]?.company_name || '';
        const companyNames = recruiterText.split('\n').filter(name => name.trim() !== '');
        const processedRecruiters = companyNames.map(name => ({
            company_name: name.trim()
        }));

        const formData = {
            ...data,
            banner: bannerImages,
            gallery: galleryImages,
            top_recruiters: processedRecruiters,
            courses: courses,
            approvals: data.approvals || [],
            // Handle fee_range
            fee_range: {
                type: data.fee_range_type
            },
            // Handle logo
            logo: {
                url: data.logo_url
            }
        }
        // Remove the temporary fields
        delete formData.fee_range_type
        delete formData.logo_url
        onSubmit(formData)
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {initialData ? 'Edit University' : 'Add New University'}
                    </h2>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex">
                    {/* Sidebar Tabs */}
                    <div className="w-64 bg-gray-50 border-r border-gray-200">
                        <nav className="p-4 space-y-2">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id
                                        ? 'bg-primary-100 text-primary-700'
                                        : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                >
                                    {tab.name}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Form Content */}
                    <div className="flex-1 overflow-y-auto max-h-[calc(90vh-120px)]">
                        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-6">
                            {/* Basic Info Tab */}
                            {activeTab === 'basic' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                University Name *
                                            </label>
                                            <input
                                                type="text"
                                                {...register('name', { required: 'University name is required' })}
                                                className="input"
                                                placeholder="Enter university name"
                                            />
                                            {errors.name && (
                                                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Fee Range Type *
                                            </label>
                                            <select
                                                {...register('fee_range_type', { required: 'Fee range type is required' })}
                                                className="input"
                                            >
                                                <option value="">Select Fee Range</option>
                                                <option value="below_2_lakhs">Below 2 Lakhs</option>
                                                <option value="above_2_lakhs">Above 2 Lakhs</option>
                                            </select>
                                            {errors.fee_range_type && (
                                                <p className="mt-1 text-sm text-red-600">{errors.fee_range_type.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Ordering Number (Lesser is top)
                                            </label>
                                            <input
                                                type="number"
                                                {...register('order', { valueAsNumber: true })}
                                                className="input"
                                                placeholder="Enter order (e.g., 1, 2, 3)"
                                            />
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    {...register('featured')}
                                                    className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                                                />
                                                <span className="text-sm font-medium text-gray-700">
                                                    Featured University (Show on homepage)
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Logo
                                        </label>
                                        <ImageUpload
                                            value={watch('logo_url') || ''}
                                            onChange={(value) => setValue('logo_url', value)}
                                            placeholder="Upload university logo (Recommended: 200x200px, Square format)"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Banner Images
                                        </label>
                                        <div className="space-y-4">
                                            {bannerImages.map((banner, index) => (
                                                <div key={index} className="border border-gray-200 rounded-lg p-4">
                                                    <div className="flex justify-between items-center mb-4">
                                                        <h4 className="font-medium text-gray-900">Banner {index + 1}</h4>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeBannerImage(index)}
                                                            className="text-red-600 hover:text-red-800"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                    <div className="space-y-4">
                                                        <div>
                                                            <ImageUpload
                                                                value={banner.url}
                                                                onChange={(value) => updateBannerImage(index, 'url', value)}
                                                                placeholder="Upload banner image (Recommended: 1200x400px, Wide format)"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                Caption
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={banner.caption}
                                                                onChange={(e) => updateBannerImage(index, 'caption', e.target.value)}
                                                                className="input"
                                                                placeholder="Enter caption"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={addBannerImage}
                                                className="btn-primary btn-sm"
                                            >
                                                Add Banner Image
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Approvals
                                        </label>
                                        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3">
                                            {approvals.map((approval) => (
                                                <label key={approval._id} className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        value={approval._id}
                                                        {...register('approvals')}
                                                        className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                                                    />
                                                    <span className="text-sm text-gray-700">{approval.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                        {approvals.length === 0 && (
                                            <p className="text-sm text-gray-500 mt-2">No approvals available. Please add approvals in the admin panel first.</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Content Tab */}
                            {activeTab === 'content' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Introduction  *
                                        </label>
                                        <RichTextEditor
                                            value={watch('introduction') || ''}
                                            onChange={(value) => setValue('introduction', value)}
                                            placeholder="Enter university introduction with rich text formatting"
                                            height="200px"
                                        />
                                        {errors.introduction && (
                                            <p className="mt-1 text-sm text-red-600">{errors.introduction.message}</p>
                                        )}
                                    </div>

                                    <div className='mt-2'>
                                        <label className="block text-sm font-medium text-gray-700 mb-2 mt-4 pt-2">
                                            Admission Information
                                        </label>
                                        <RichTextEditor
                                            value={watch('admission') || ''}
                                            onChange={(value) => setValue('admission', value)}
                                            placeholder="Enter admission information with rich text formatting"
                                            height="200px"
                                        />
                                        {errors.admission && (
                                            <p className="mt-1 text-sm text-red-600">{errors.admission.message}</p>
                                        )}
                                    </div>

                                    <div className='mt-2'>
                                        <label className="block text-sm font-medium text-gray-700 mb-2 mt-4 pt-2">
                                            Placement Information
                                        </label>
                                        <RichTextEditor
                                            value={watch('placements') || ''}
                                            onChange={(value) => setValue('placements', value)}
                                            placeholder="Enter placement information with rich text formatting"
                                            height="200px"
                                        />
                                        {errors.placements && (
                                            <p className="mt-1 text-sm text-red-600">{errors.placements.message}</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Courses Tab */}
                            {activeTab === 'courses' && (
                                <div className="space-y-6">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-lg font-medium text-gray-900">University Courses</h3>
                                        {!showCourseForm && (
                                            <button
                                                type="button"
                                                onClick={addCourse}
                                                className="btn-primary btn-sm"
                                            >
                                                Add Course
                                            </button>
                                        )}
                                    </div>

                                    {showCourseForm ? (
                                        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                                            <div className="flex justify-between items-center mb-6">
                                                <h4 className="text-lg font-medium text-gray-900">
                                                    {editingCourse ? 'Edit Course' : 'Add New Course'}
                                                </h4>
                                                <button
                                                    type="button"
                                                    onClick={handleCourseCancel}
                                                    className="text-gray-400 hover:text-gray-600"
                                                >
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>

                                            {/* Course Form with Tabs */}
                                            <div className="space-y-6">
                                                {/* Basic Information */}
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Level *
                                                        </label>
                                                        <select
                                                            value={courseFormData.level}
                                                            onChange={(e) => updateCourseFormData('level', e.target.value)}
                                                            className="input"
                                                        >
                                                            <option value="">Select Level</option>
                                                            <option value="Diploma">Diploma</option>
                                                            <option value="Undergraduate (UG)">Undergraduate (UG)</option>
                                                            <option value="Postgraduate (PG)">Postgraduate (PG)</option>
                                                            <option value="Certificate">Certificate</option>

                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Degree *
                                                        </label>
                                                        <select
                                                            value={courseFormData.degreeId}
                                                            onChange={(e) => updateCourseFormData('degreeId', e.target.value)}
                                                            className="input"
                                                            disabled={!courseFormData.level}
                                                        >
                                                            <option value="">
                                                                {!courseFormData.level
                                                                    ? "Select a level first"
                                                                    : "Select Degree"
                                                                }
                                                            </option>
                                                            {degrees.filter(degree => degree.level === courseFormData.level).length === 0 ? (
                                                                <option value="" disabled>
                                                                    No degrees available for {courseFormData.level}
                                                                </option>
                                                            ) : (
                                                                degrees.filter(degree => degree.level === courseFormData.level).map((degree) => (
                                                                    <option key={degree._id} value={degree._id}>
                                                                        {degree.name}
                                                                    </option>
                                                                ))
                                                            )}
                                                        </select>
                                                    </div>



                                                    <div className="lg:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Specializations *
                                                        </label>
                                                        <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3">
                                                            {!courseFormData.degreeId ? (
                                                                <p className="text-sm text-gray-500 text-center py-4">
                                                                    Please select a degree first to view available specializations
                                                                </p>
                                                            ) : specializations.length === 0 ? (
                                                                <p className="text-sm text-gray-500 text-center py-4">
                                                                    No specializations available for the selected degree
                                                                </p>
                                                            ) : (
                                                                specializations.map((specialization) => (
                                                                    <label key={specialization._id} className="flex items-center space-x-2 mb-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={courseFormData.specialization.includes(specialization.name)}
                                                                            onChange={(e) => {
                                                                                const newSpecializations = e.target.checked
                                                                                    ? [...courseFormData.specialization, specialization.name]
                                                                                    : courseFormData.specialization.filter(s => s !== specialization.name)
                                                                                updateCourseFormData('specialization', newSpecializations)
                                                                            }}
                                                                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                                        />
                                                                        <span className="text-sm text-gray-700">{specialization.name}</span>
                                                                    </label>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Duration *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={courseFormData.duration}
                                                            onChange={(e) => updateCourseFormData('duration', e.target.value)}
                                                            className="input"
                                                            placeholder={courseFormData.degreeId && degrees.find(d => d._id === courseFormData.degreeId)
                                                                ? degrees.find(d => d._id === courseFormData.degreeId).duration
                                                                : "Select a degree first to see duration"
                                                            }
                                                            disabled={!courseFormData.degreeId}
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="flex items-center space-x-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={courseFormData.featured}
                                                                onChange={(e) => updateCourseFormData('featured', e.target.checked)}
                                                                className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                                                            />
                                                            <span className="text-sm font-medium text-gray-700">
                                                                Featured Course
                                                            </span>
                                                        </label>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Ordering Number
                                                        </label>
                                                        <input
                                                            type="number"
                                                            value={courseFormData.order}
                                                            onChange={(e) => updateCourseFormData('order', parseInt(e.target.value) || 0)}
                                                            className="input"
                                                            placeholder="Enter order (e.g., 1, 2, 3)"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Rich Text Editors */}
                                                <div className="space-y-6">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Curriculum *
                                                        </label>
                                                        <RichTextEditor
                                                            value={courseFormData.curriculum || ''}
                                                            onChange={(value) => updateCourseFormData('curriculum', value)}
                                                            placeholder="Enter detailed curriculum information with subjects, semesters, and course structure"
                                                            height="200px"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Eligibility Criteria *
                                                        </label>
                                                        <RichTextEditor
                                                            value={courseFormData.eligibility || ''}
                                                            onChange={(value) => updateCourseFormData('eligibility', value)}
                                                            placeholder="Enter eligibility criteria including educational qualifications, entrance exams, and other requirements"
                                                            height="200px"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Fee Structure *
                                                        </label>
                                                        <RichTextEditor
                                                            value={courseFormData.feeStructure || ''}
                                                            onChange={(value) => updateCourseFormData('feeStructure', value)}
                                                            placeholder="Enter detailed fee structure including tuition fees, additional charges, payment options, and fee breakdown"
                                                            height="200px"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            Scholarships & Financial Aid
                                                        </label>
                                                        <RichTextEditor
                                                            value={courseFormData.scholarships || ''}
                                                            onChange={(value) => updateCourseFormData('scholarships', value)}
                                                            placeholder="Enter information about available scholarships, financial aid, and funding opportunities"
                                                            height="200px"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
                                                <button
                                                    type="button"
                                                    onClick={handleCourseCancel}
                                                    className="btn-outline"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleCourseSubmit}
                                                    className="btn-primary"
                                                >
                                                    {editingCourse ? 'Update Course' : 'Add Course'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {courses.length === 0 ? (
                                                <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                    </svg>
                                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No courses added</h3>
                                                    <p className="mt-1 text-sm text-gray-500">Get started by adding the first course for this university.</p>
                                                    <div className="mt-6">
                                                        <button
                                                            type="button"
                                                            onClick={addCourse}
                                                            className="btn-primary"
                                                        >
                                                            Add Course
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="grid gap-4">
                                                    {courses.map((course, index) => (
                                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                                            <div className="flex justify-between items-start">
                                                                <div className="flex-1">
                                                                    <div className="flex items-center space-x-3 mb-2">
                                                                        {course.level && (
                                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                                {course.level}
                                                                            </span>
                                                                        )}
                                                                        {course.degreeId && degrees.find(d => d._id === course.degreeId) && (
                                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                {degrees.find(d => d._id === course.degreeId).name}
                                                                            </span>
                                                                        )}
                                                                        {course.featured && (
                                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                                                Featured
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                                                                        {course.duration && (
                                                                            <span>Duration: {course.duration}</span>
                                                                        )}
                                                                        {course.specialization && course.specialization.length > 0 && (
                                                                            <span>Specialization: {course.specialization.join(', ')}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex space-x-2 ml-4">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => editCourse(course)}
                                                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeCourse(index)}
                                                                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Top Recruiters Tab */}
                            {activeTab === 'recruiters' && (
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-4">Top Recruiters</h3>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Company Names
                                            </label>
                                            <textarea
                                                value={topRecruiters[0]?.company_name || ''}
                                                onChange={(e) => updateTopRecruiter(0, 'company_name', e.target.value)}
                                                className="input"
                                                rows={8}
                                                placeholder="Enter company names (one per line)&#10;Example:&#10;Google&#10;Microsoft&#10;Amazon&#10;TCS&#10;Infosys"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Gallery Tab */}
                            {activeTab === 'gallery' && (
                                <div className="space-y-6">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-lg font-medium text-gray-900">Gallery Images</h3>
                                        <button
                                            type="button"
                                            onClick={addGalleryImage}
                                            className="btn-primary btn-sm"
                                        >
                                            Add Image
                                        </button>
                                    </div>

                                    {galleryImages.map((image, index) => (
                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="font-medium text-gray-900">Image {index + 1}</h4>
                                                <button
                                                    type="button"
                                                    onClick={() => removeGalleryImage(index)}
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                            <div className="space-y-4">
                                                <div>
                                                    <ImageUpload
                                                        value={image.url}
                                                        onChange={(value) => updateGalleryImage(index, 'url', value)}
                                                        placeholder="Upload gallery image (Recommended: 800x600px, Standard format)"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Caption
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={image.caption}
                                                            onChange={(e) => updateGalleryImage(index, 'caption', e.target.value)}
                                                            className="input"
                                                            placeholder="Enter caption"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Type
                                                        </label>
                                                        <select
                                                            value={image.type}
                                                            onChange={(e) => updateGalleryImage(index, 'type', e.target.value)}
                                                            className="input"
                                                        >
                                                            <option value="campus">Campus</option>
                                                            <option value="infrastructure">Infrastructure</option>
                                                            <option value="events">Events</option>
                                                            <option value="students">Students</option>
                                                            <option value="faculty">Faculty</option>
                                                            <option value="other">Other</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Form Actions */}
                            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={onCancel}
                                    className="btn-outline"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                >
                                    {initialData ? 'Update University' : 'Create University'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

        </div>
    )
}

export default UniversityForm